import { createRouter, createWebHistory } from 'vue-router'

import { Permission } from '@/enums/permission'
import { landingRoute, mustChangePassword, routeAllowed } from '@/libs/access'
import { isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'

/*
 * What a route says about itself: whether it needs a session, its layout, and the permission it
 * needs (`libs/access.ts`) — one, or several of which any one will do.
 */
declare module 'vue-router' {
    interface RouteMeta {
        requiresAuth?: boolean
        layout?:       'Default' | 'Blank'
        permission?:   Permission | Permission[]
    }
}

/*
 * The signed-in shell is exactly the viewport (`layouts/Default.vue`, `h-dvh`) and its `<main>`
 * is what scrolls — the window never does. So a navigation to another page scrolls THAT element
 * back to the top; a change of the query only (a tab, a filter) leaves it where it is.
 */
const shellMain = (): HTMLElement | null => document.querySelector<HTMLElement>('main')

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        const main = shellMain()

        if (!main) {
            if (savedPosition)         return savedPosition
            if (from.path === to.path) return {}

            return { top: 0 }
        }

        if (from.path !== to.path) main.scrollTo({ top: 0 })

        return false
    },
    routes: [
        {
            path:     '/',
            name:     'landing',
            redirect: () => landingRoute(),
        },
        {
            path:      '/login',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            path:      '/dashboard',
            name:      'dashboard',
            component: () => import('@/views/dashboard/Dashboard.vue'),
            meta:      { requiresAuth: true },
        },
        // Dobavnice → Nalaganje: drop scans, follow their reading.
        {
            path:      '/dobavnice/nalaganje',
            name:      'uploads',
            component: () => import('@/views/upload/Uploads.vue'),
            meta:      { requiresAuth: true, permission: Permission.DobavniceUpload },
        },
        // Dobavnice → Seznam: `?status=` picks the tab, the filters ride in the query too.
        {
            path:      '/dobavnice/seznam',
            name:      'notes',
            component: () => import('@/views/note/Notes.vue'),
            meta:      { requiresAuth: true, permission: Permission.DobavniceView },
        },
        // One delivery note: the scan beside the form — the reviewer's screen.
        {
            path:      '/dobavnice/seznam/:id',
            name:      'note',
            component: () => import('@/views/note/Note.vue'),
            meta:      { requiresAuth: true, permission: Permission.DobavniceView },
        },
        {
            path:      '/dobavnice/izvoz',
            name:      'exports',
            component: () => import('@/views/export/Exports.vue'),
            meta:      { requiresAuth: true, permission: Permission.DobavniceExport },
        },
        // Dobavitelji (Davor, 2026-10-05: their own page): the list, and one supplier with its learned mappings.
        {
            path:      '/dobavnice/dobavitelji',
            name:      'suppliers',
            component: () => import('@/views/supplier/Suppliers.vue'),
            meta:      { requiresAuth: true, permission: Permission.DobavniceView },
        },
        {
            path:      '/dobavnice/dobavitelji/:id',
            name:      'supplier',
            component: () => import('@/views/supplier/Supplier.vue'),
            meta:      { requiresAuth: true, permission: Permission.DobavniceView },
        },
        // Šifranti: `?tab=materials|warehouses|mappings` — the material catalog first.
        {
            path:      '/dobavnice/sifranti',
            name:      'catalogs',
            component: () => import('@/views/catalog/Catalogs.vue'),
            meta:      { requiresAuth: true, permission: Permission.DobavniceView },
        },
        // Artikli šanka: each bar place's ordered article list, and the whole catalog (edit with `inventura.articles`).
        {
            path:      '/inventura/artikli',
            name:      'articles',
            component: () => import('@/views/article/Articles.vue'),
            meta:      { requiresAuth: true, permission: [Permission.InventuraView, Permission.PrenosView] },
        },
        // Inventura: the list of stock counts (`?status=` picks the tab; lokal and dates in the query too).
        {
            path:      '/inventura',
            name:      'inventories',
            component: () => import('@/views/inventura/Inventories.vue'),
            meta:      { requiresAuth: true, permission: Permission.InventuraView },
        },
        // One inventory: the manager's review (book vs counted); a counter gets the counting screen while it is open.
        {
            path:      '/inventura/:id',
            name:      'inventory',
            component: () => import('@/views/inventura/Inventory.vue'),
            meta:      { requiresAuth: true, permission: Permission.InventuraView },
        },
        // The counting screen — phones in the kitchen and behind the bar; blind (no book quantities).
        {
            path:      '/inventura/:id/stetje',
            name:      'inventory-count',
            component: () => import('@/views/inventura/Count.vue'),
            meta:      { requiresAuth: true, permission: Permission.InventuraView },
        },
        // Prenos: the transfer notes, a tab per status (`?status=`), place, dates and article in the query.
        {
            path:      '/prenos',
            name:      'transfers',
            component: () => import('@/views/transfer/Transfers.vue'),
            meta:      { requiresAuth: true, permission: Permission.PrenosView },
        },
        // A new transfer note — a phone in the warehouse first.
        {
            path:      '/prenos/nova',
            name:      'transfer-new',
            component: () => import('@/views/transfer/TransferForm.vue'),
            meta:      { requiresAuth: true, permission: Permission.PrenosCreate },
        },
        {
            path:      '/prenos/:id',
            name:      'transfer',
            component: () => import('@/views/transfer/Transfer.vue'),
            meta:      { requiresAuth: true, permission: Permission.PrenosView },
        },
        {
            path:      '/prenos/:id/uredi',
            name:      'transfer-edit',
            component: () => import('@/views/transfer/TransferForm.vue'),
            meta:      { requiresAuth: true, permission: Permission.PrenosCreate },
        },
        // The A4 form that travels with the goods — no shell, black on white.
        {
            path:      '/prenos/:id/natisni',
            name:      'transfer-print',
            component: () => import('@/views/transfer/TransferPrint.vue'),
            meta:      { requiresAuth: true, layout: 'Blank', permission: Permission.PrenosView },
        },
        // Urniki: the week's schedule (`/urniki` = this week); a person with only `urniki.view` sees published weeks.
        {
            path:      '/urniki/:monday(\\d{4}-\\d{2}-\\d{2})?',
            name:      'schedule',
            component: () => import('@/views/urniki/Schedule.vue'),
            meta:      { requiresAuth: true, permission: Permission.UrnikiView },
        },
        // The week on paper — A4 landscape, the grid in the places' colours; no shell.
        {
            path:      '/urniki/:monday(\\d{4}-\\d{2}-\\d{2})/natisni',
            name:      'schedule-print',
            component: () => import('@/views/urniki/SchedulePrint.vue'),
            meta:      { requiresAuth: true, layout: 'Blank', permission: Permission.UrnikiView },
        },
        // My upcoming shifts — a phone first.
        {
            path:      '/urniki/moj',
            name:      'my-schedule',
            component: () => import('@/views/urniki/MySchedule.vue'),
            meta:      { requiresAuth: true, permission: Permission.UrnikiView },
        },
        {
            path:      '/urniki/zaposleni',
            name:      'employees',
            component: () => import('@/views/urniki/Employees.vue'),
            meta:      { requiresAuth: true, permission: Permission.UrnikiEdit },
        },
        // E-računi → Dnevnik: every file of the e-računi folder, what was e-mailed, resend (`eracuni.manage`).
        {
            path:      '/eracuni/dnevnik',
            name:      'eracuni-log',
            component: () => import('@/views/eracuni/EracuniLog.vue'),
            meta:      { requiresAuth: true, permission: Permission.EracuniView },
        },
        {
            path:      '/administracija/uporabniki',
            name:      'users',
            component: () => import('@/views/user/Users.vue'),
            meta:      { requiresAuth: true, permission: Permission.AdminUsers },
        },
        {
            path:      '/administracija/vloge',
            name:      'roles',
            component: () => import('@/views/role/Roles.vue'),
            meta:      { requiresAuth: true, permission: Permission.AdminUsers },
        },
        // Nastavitve: `?tab=ai|posta|dobavnice|skener|inventura|urniki|eracuni`, each tab shown by its own permission.
        {
            path:      '/administracija/nastavitve',
            name:      'settings',
            component: () => import('@/views/settings/Settings.vue'),
            meta:      { requiresAuth: true, permission: [Permission.AdminSettings, Permission.DobavniceSettings, Permission.InventuraManage, Permission.UrnikiEdit, Permission.EracuniManage] },
        },
        {
            path:      '/profil',
            name:      'profile',
            component: () => import('@/views/profile/Profile.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:     '/:pathMatch(.*)*',
            redirect: () => landingRoute(),
        },
    ],
})

/*
 * Signed out → the login, with the way back. Signed in → never the login again; and once /me is
 * known, a page the person may not open gives way to the landing, and a password somebody else
 * set gives way to the profile (App.vue makes the same checks when /me arrives after the first
 * navigation).
 */
router.beforeEach((to) => {
    const appStore = useAppStore()
    const loggedIn = isAuthenticated()

    if (loggedIn && to.name === 'login') return landingRoute()

    if (to.meta.requiresAuth && !loggedIn) {
        appStore.setLogout()

        return { name: 'login', query: to.fullPath !== '/' ? { redirect: to.fullPath } : {} }
    }

    if (to.meta.requiresAuth && mustChangePassword() && to.name !== 'profile') return { name: 'profile' }
    if (to.meta.requiresAuth && !routeAllowed(to.meta)) return landingRoute()

    return true
})

export default router
